/**
 * Shared helpers for seeding a create-form from a record the user already
 * chose, so the same data is never typed twice.
 *
 * The rule everywhere: seed only fields the user has not filled in. A prefill
 * is a starting point, never an override — every field stays editable, and a
 * value the user typed is never clobbered by a later fetch resolving.
 */

/** The five address fields as they are named on Job and Opportunity. */
export interface AddressFields {
  address_1: string;
  address_2: string;
  city: string;
  state: string;
  zip_code: string;
}

/**
 * Optional geocode that rides along with an address when we have one.
 *
 * Left as string|number because the two payloads disagree: DRF renders the
 * Address decimal fields as strings, while parts of the frontend already type
 * them as numbers. Callers coerce at the point of use.
 */
export interface GeocodeFields {
  latitude?: string | number | null;
  longitude?: string | number | null;
}

/**
 * A contact's primary address, as returned by `primary_address_detail` on both
 * the CRM list and detail endpoints.
 */
interface PrimaryAddressDetailLike {
  street_address_1?: string | null;
  street_address_2?: string | null;
  city?: string | null;
  state_province?: string | null;
  postal_code?: string | null;
  latitude?: string | number | null;
  longitude?: string | number | null;
  is_primary?: boolean;
}

interface ContactLike {
  primary_address_detail?: PrimaryAddressDetailLike | null;
  addresses?: PrimaryAddressDetailLike[] | null;
}

/** A record that carries the flat address shape (Job, Opportunity). */
interface AddressRecordLike {
  address_1?: string | null;
  address_2?: string | null;
  city?: string | null;
  state?: string | null;
  zip_code?: string | null;
}

const str = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value);

const US_STATE_CODES: Record<string, string> = {
  alabama: 'AL', alaska: 'AK', arizona: 'AZ', arkansas: 'AR', california: 'CA',
  colorado: 'CO', connecticut: 'CT', delaware: 'DE', 'district of columbia': 'DC',
  florida: 'FL', georgia: 'GA', hawaii: 'HI', idaho: 'ID', illinois: 'IL',
  indiana: 'IN', iowa: 'IA', kansas: 'KS', kentucky: 'KY', louisiana: 'LA',
  maine: 'ME', maryland: 'MD', massachusetts: 'MA', michigan: 'MI', minnesota: 'MN',
  mississippi: 'MS', missouri: 'MO', montana: 'MT', nebraska: 'NE', nevada: 'NV',
  'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY',
  'north carolina': 'NC', 'north dakota': 'ND', ohio: 'OH', oklahoma: 'OK',
  oregon: 'OR', pennsylvania: 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
  'south dakota': 'SD', tennessee: 'TN', texas: 'TX', utah: 'UT', vermont: 'VT',
  virginia: 'VA', washington: 'WA', 'west virginia': 'WV', wisconsin: 'WI', wyoming: 'WY',
};

/**
 * Contact addresses store `state_province` as typed — "Pennsylvania" as often
 * as "PA" — but the job and opportunity forms pick the state from two-letter
 * codes, so a full name would seed a value the dropdown can't show. Anything
 * that isn't a US state name (already a code, a province) passes through.
 */
export function toStateCode(state: string): string {
  return US_STATE_CODES[state.trim().toLowerCase()] ?? state;
}

/**
 * Pull the primary address off a contact, accepting either payload shape:
 * `primary_address_detail` (list + detail endpoints) or the full `addresses`
 * array (detail endpoint). Returns null when the contact has no address.
 */
export function contactAddressFields(
  contact: ContactLike | null | undefined,
): (AddressFields & GeocodeFields) | null {
  if (!contact) return null;

  const addresses = contact.addresses ?? [];
  const address =
    contact.primary_address_detail ??
    addresses.find((a) => a.is_primary) ??
    addresses[0];

  if (!address) return null;

  const fields: AddressFields & GeocodeFields = {
    address_1: str(address.street_address_1),
    address_2: str(address.street_address_2),
    city: str(address.city),
    state: toStateCode(str(address.state_province)),
    zip_code: str(address.postal_code),
    latitude: address.latitude ?? null,
    longitude: address.longitude ?? null,
  };

  // An address row with no street and no city carries nothing worth seeding.
  if (!fields.address_1 && !fields.city) return null;

  return fields;
}

/** Pull the flat address off a Job or Opportunity. */
export function recordAddressFields(
  record: AddressRecordLike | null | undefined,
): AddressFields | null {
  if (!record) return null;

  const fields: AddressFields = {
    address_1: str(record.address_1),
    address_2: str(record.address_2),
    city: str(record.city),
    state: str(record.state),
    zip_code: str(record.zip_code),
  };

  if (!fields.address_1 && !fields.city) return null;

  return fields;
}

/** Render an address as the single line a Google Places search would produce. */
export function formatAddressLine(
  fields: Partial<AddressFields> | null | undefined,
): string {
  if (!fields) return '';
  const street = [fields.address_1, fields.address_2].filter(Boolean).join(' ');
  const locality = [fields.city, fields.state].filter(Boolean).join(', ');
  return [street, locality, fields.zip_code].filter(Boolean).join(' ').trim();
}

/**
 * Split a one-line address back into its parts.
 *
 * Accepts the canonical Google `formatted_address` shape -
 * "123 Main St, Scranton, PA 18503" with an optional trailing country - and
 * returns null for anything it cannot read confidently. Callers use this to
 * back-fill a record that has no address, so returning null (skip the
 * back-fill) is always safer than guessing wrong and writing a mangled
 * address onto a real record.
 */
export function parseAddressLine(
  line: string | null | undefined,
): Pick<AddressFields, 'address_1' | 'city' | 'state' | 'zip_code'> | null {
  if (!line) return null;

  const parts = line
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

  // Drop a trailing country segment ("USA", "United States").
  if (parts.length > 3 && /^(usa|united states)$/i.test(parts[parts.length - 1])) {
    parts.pop();
  }

  if (parts.length < 3) return null;

  // The last segment must read "ST 12345" or "ST 12345-6789".
  const stateZip = parts[parts.length - 1].match(/^([A-Za-z]{2})\s+(\d{5}(?:-\d{4})?)$/);
  if (!stateZip) return null;

  const city = parts[parts.length - 2];
  const street = parts.slice(0, parts.length - 2).join(', ');
  if (!street || !city) return null;

  return {
    address_1: street,
    city,
    state: stateZip[1].toUpperCase(),
    zip_code: stateZip[2],
  };
}

/**
 * Merge `incoming` into `prev`, writing only where `prev` is still empty.
 *
 * Use inside a `setFormData(prev => ...)` updater so the decision is made
 * against the freshest state — a prefill that resolves after the user has
 * started typing must not overwrite what they typed.
 */
export function applyPrefill<T extends object>(
  prev: T,
  incoming: Partial<T>,
): T {
  const next = { ...prev };
  let changed = false;

  (Object.keys(incoming) as (keyof T)[]).forEach((key) => {
    const value = incoming[key];
    if (value === undefined || value === null || value === '') return;

    const current = next[key];
    const isEmpty =
      current === undefined ||
      current === null ||
      current === '' ||
      (Array.isArray(current) && current.length === 0);

    if (isEmpty) {
      next[key] = value as T[keyof T];
      changed = true;
    }
  });

  // Preserve referential identity when nothing was seeded, so callers can run
  // this from an effect without triggering a redundant re-render.
  return changed ? next : prev;
}
